import { type ReactNode } from "react";
import { PanelLeft, PanelRight, X } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useWorkspace } from "@/hooks/useWorkspace";
import { ChatRail } from "./shell/ChatRail";
import { RightRailContent } from "./shell/RightRailContent";
import {
  BOTTOM_DOCK_HEIGHT_KEY,
  BOTTOM_TABS,
  RAIL_WIDTH_KEY,
  useViewportWidth,
} from "./shell/shellState";
import { useShellLayoutState, persistSize, readSize } from "./shell/useShellLayoutState";
import { BottomDockContent, type BottomDockPanels, type BottomDockCounts } from "./shell/BottomDockContent";
import { BottomTabBar } from "./shell/BottomTabBar";

type ComposerShellProps = {
  workspaceLabel?: string;
  workspaceId?: string;
  topBarActions?: ReactNode;
  bottomDockPanels?: BottomDockPanels;
  bottomDockCounts?: BottomDockCounts;
  draftsPanel?: ReactNode;
  outboxPanel?: ReactNode;
  libraryPanel?: ReactNode;
  excerptsPanel?: ReactNode;
  bibliographyPanel?: ReactNode;
  children: ReactNode;
};

export const ComposerShell = ({
  workspaceLabel,
  workspaceId,
  topBarActions,
  bottomDockPanels,
  bottomDockCounts,
  draftsPanel,
  outboxPanel,
  libraryPanel,
  excerptsPanel,
  bibliographyPanel,
  children,
}: ComposerShellProps) => {
  const viewport = useViewportWidth();
  const isMobile = useIsMobile();
  const autoCollapseRight = viewport < 1280;
  const autoCollapseLeft = viewport < 1024;

  const { active } = useWorkspace();
  const codexEnabled = active?.codex_enabled !== false;
  const visibleBottomTabs = codexEnabled ? BOTTOM_TABS : BOTTOM_TABS.filter((t) => t.id !== "codex");

  const { leftOpen, setLeftOpen, rightOpen, setRightOpen, bottomDockTab: rawBottomDockTab, setBottomDockTab } =
    useShellLayoutState(workspaceId, autoCollapseLeft, autoCollapseRight, codexEnabled);
  // Composer bottom dock (Codex/Builder/Vault/Corpus) is desktop-only — see mobile-ux-stance.md
  const bottomDockTab = isMobile ? null : rawBottomDockTab;

  const leftSize = leftOpen ? readSize(RAIL_WIDTH_KEY(workspaceId, "left"), 24) : 0;
  const rightSize = rightOpen ? readSize(RAIL_WIDTH_KEY(workspaceId, "right"), 26) : 0;
  const centerSize = Math.max(30, 100 - leftSize - rightSize);

  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex h-full w-full flex-col">
        {/* Top bar */}
        <div className="flex h-12 shrink-0 items-center gap-2 border-b border-border/60 bg-background/80 px-3 backdrop-blur">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setLeftOpen((v) => !v)}
                aria-label={leftOpen ? "Collapse chat rail" : "Expand chat rail"}
                className="h-8 w-8"
              >
                <PanelLeft className={cn("h-4 w-4", !leftOpen && "opacity-60")} />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">{leftOpen ? "Collapse chat rail" : "Expand chat rail"}</TooltipContent>
          </Tooltip>

          <div className="flex min-w-0 items-center gap-2">
            <span className="truncate text-sm font-medium">Composer</span>
            {workspaceLabel && (
              <>
                <span className="text-xs text-muted-foreground">·</span>
                <span className="truncate text-xs text-muted-foreground">{workspaceLabel}</span>
              </>
            )}
          </div>

          <div className="ml-auto flex items-center gap-2">
            {topBarActions}

            {!isMobile && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setRightOpen((v) => !v)}
                    aria-label={rightOpen ? "Collapse reference rail" : "Expand reference rail"}
                    className="h-8 w-8"
                  >
                    <PanelRight className={cn("h-4 w-4", !rightOpen && "opacity-60")} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  {rightOpen ? "Collapse reference rail" : "Expand reference rail"}
                </TooltipContent>
              </Tooltip>
            )}
          </div>

        </div>

        {/* 3-pane body */}
        <div className="relative flex min-h-0 flex-1">
          <ResizablePanelGroup direction="horizontal" className="h-full w-full">
            {leftOpen && !isMobile && (
              <>
                <ResizablePanel
                  defaultSize={leftSize}
                  minSize={16}
                  maxSize={40}
                  onResize={persistSize(RAIL_WIDTH_KEY(workspaceId, "left"))}
                  className="bg-muted/20"
                >
                  <ChatRail />
                </ResizablePanel>
                <ResizableHandle withHandle />
              </>
            )}

            <ResizablePanel
              defaultSize={centerSize}
              minSize={30}
            >
              <div className="flex h-full w-full flex-col">
                <div className="min-h-0 flex-1">
                  <ResizablePanelGroup direction="vertical" className="h-full w-full">
                    <ResizablePanel defaultSize={bottomDockTab ? 65 : 100} minSize={30}>
                      <div className="h-full overflow-hidden">
                        <div className="flex h-full w-full flex-col px-5 py-5">{children}</div>
                      </div>
                    </ResizablePanel>
                    {bottomDockTab ? (
                      <>
                        <ResizableHandle withHandle />
                        <ResizablePanel
                          defaultSize={readSize(BOTTOM_DOCK_HEIGHT_KEY(workspaceId), 35)}
                          minSize={15}
                          maxSize={70}
                          onResize={persistSize(BOTTOM_DOCK_HEIGHT_KEY(workspaceId))}
                          className="bg-muted/20"
                        >
                          <BottomDockContent
                            workspaceId={workspaceId}
                            bottomDockTab={bottomDockTab}
                            setBottomDockTab={setBottomDockTab}
                            panels={bottomDockPanels}
                            counts={bottomDockCounts}
                          />
                        </ResizablePanel>
                      </>
                    ) : null}
                  </ResizablePanelGroup>
                </div>
                {!isMobile && (
                  <BottomTabBar
                    visibleBottomTabs={visibleBottomTabs}
                    bottomDockTab={bottomDockTab}
                    setBottomDockTab={setBottomDockTab}
                    counts={bottomDockCounts}
                  />
                )}
              </div>
            </ResizablePanel>

            {rightOpen && !isMobile && (
              <>
                <ResizableHandle withHandle />
                <ResizablePanel
                  defaultSize={rightSize}
                  minSize={18}
                  maxSize={40}
                  onResize={persistSize(RAIL_WIDTH_KEY(workspaceId, "right"))}
                  className="bg-muted/20"
                >
                  <RightRailContent
                    draftsPanel={draftsPanel}
                    outboxPanel={outboxPanel}
                    libraryPanel={libraryPanel}
                    excerptsPanel={excerptsPanel}
                    bibliographyPanel={bibliographyPanel}
                  />
                </ResizablePanel>
              </>
            )}
          </ResizablePanelGroup>

          {isMobile && leftOpen && (
            <div className="fixed inset-0 z-50 flex flex-col bg-background">
              <div className="flex h-12 shrink-0 items-center justify-between border-b border-border/60 px-3">
                <span className="text-sm font-medium">Chat</span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setLeftOpen(false)}
                  aria-label="Close chat"
                  className="h-8 w-8"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="min-h-0 flex-1">
                <ChatRail />
              </div>
            </div>
          )}
        </div>
      </div>
    </TooltipProvider>

  );
};

export default ComposerShell;
